import { useEffect, useCallback, useRef } from 'react';
import { usePlansStore, nextDetailRequestId } from '../stores/usePlansStore';
import type { PlansFilterParams } from '../services/plansApi';

/**
 * Hook for the Discover tab — fetches discovery feeds + community plans.
 */
export function usePlansDiscover() {
  const store = usePlansStore();

  useEffect(() => {
    store.fetchDiscoveryFeeds();
    store.fetchCommunityPlans();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = useCallback((filters: PlansFilterParams) => {
    store.setFilters(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    featured: store.featured,
    trending: store.trending,
    perfectTiming: store.perfectTiming,
    communityPlans: store.communityPlans,
    filters: store.filters,
    isLoadingDiscovery: store.isLoadingDiscovery,
    isLoadingPlans: store.isLoadingPlans,
    error: store.error,
    sectionErrors: store.sectionErrors,
    applyFilters,
    clearError: store.clearError,
    refresh: () => {
      store.invalidate();
      store.fetchDiscoveryFeeds();
      store.fetchCommunityPlans();
    },
  };
}

/**
 * Hook for community plan detail — fetches by slug.
 *
 * Request-scoped: each component instance mints a request id during render,
 * once per slug, and everything it returns answers THAT request only. A plan
 * retained in the store from an earlier navigation — even one with the same
 * slug — reads as loading until this navigation's own fetch settles, so no
 * effect keyed on `plan` (a visit, a `?fork=1` resume) ever runs on it.
 */
export function usePlanDetail(slug: string | undefined) {
  const store = usePlansStore();

  const requestRef = useRef<{ slug: string | undefined; id: number } | null>(null);
  if (requestRef.current === null || requestRef.current.slug !== slug) {
    requestRef.current = { slug, id: nextDetailRequestId() };
  }
  const requestId = requestRef.current.id;

  useEffect(() => {
    if (!slug) return;
    // Read fresh, never from the render's snapshot: StrictMode's second
    // effect run must find this request already started and do nothing.
    if (usePlansStore.getState().detailRequestId === requestId) return;
    usePlansStore.getState().fetchPlanBySlug(slug, requestId);
  }, [slug, requestId]);

  const mine = store.detailRequestId === requestId;

  return {
    plan: mine ? store.activePlanDetail : null,
    // No slug, no request: nothing is loading (the page renders not-found).
    isLoading: !!slug && (!mine || store.detailStatus === 'loading'),
    notFound: mine && store.detailStatus === 'not_found',
    failed: mine && store.detailStatus === 'error',
    error: mine ? store.error : null,
    forkPlan: store.forkPlan,
    markHelpful: store.markHelpful,
    clearError: store.clearError,
  };
}
